import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState, ErrorState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { directoryQuery, nameFor } from "@/features/directory/queries";
import { decideHandover } from "@/features/submissions/api";
import { handoversQuery } from "@/features/submissions/queries";

/**
 * Handovers: the admin queue, and a reviewer's own requests.
 *
 * One route for two audiences because it is one list filtered
 * differently — `handover_predicate` draws the line server-side, so an
 * admin sees every open request and a reviewer sees only the ones they
 * are a party to. Deciding is admin-only (`handover.decide`), which is
 * DEE-24's point: a reviewer proposes the move, an administrator approves
 * it, and neither can do both.
 *
 * Approving is the *only* thing that moves `Submission.reviewer_id`. The
 * original reviewer keeps full authority right up to that moment, which
 * is why a pending handover shows no "frozen" state anywhere here.
 */
export const Route = createFileRoute("/_authed/handovers")({
  staticData: { crumbs: [{ key: "nav.handovers" }] },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(sessionQuery);
    // Names only. The list is an infinite query and loads on mount.
    await context.queryClient.ensureQueryData(directoryQuery());
  },
  component: HandoverQueue,
});

function HandoverQueue() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useSession();

  const { data: directory } = useSuspenseQuery(directoryQuery());
  const query = useInfiniteQuery(handoversQuery({ status: "pending" }));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);

  const mayDecide = can("handover.decide");

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) => decideHandover(id, approve),
    onSuccess: (_, variables) => {
      toast.success(variables.approve ? t("handovers.approved") : t("handovers.rejected"));
      void queryClient.invalidateQueries({ queryKey: ["handovers"] });
      void queryClient.invalidateQueries({ queryKey: ["submissions"] });
      void queryClient.invalidateQueries({ queryKey: ["submission-review"] });
    },
  });

  return (
    <>
      <PageHeader
        title={t("handovers.title")}
        description={t("handovers.description")}
        help={t("pageHelp.handovers")}
      />

      {query.isPending ? (
        <p className="text-sm text-muted">{t("common.loading")}</p>
      ) : query.error !== null ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title={t("handovers.empty.title")} body={t("handovers.empty.body")} />
      ) : (
        <ul className="space-y-3">
          {rows.map((handover) => (
            <li
              key={handover.id}
              className="space-y-3 rounded-panel border border-border bg-surface p-4"
            >
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={handover.status as "pending" | "approved" | "rejected"} />
                <time dateTime={handover.requested_at} className="text-xs text-muted">
                  {new Date(handover.requested_at).toLocaleString()}
                </time>
              </div>

              <p className="text-sm text-foreground">
                {t("handovers.summary", {
                  from: nameFor(directory, handover.from_reviewer_id) ?? "—",
                  to: nameFor(directory, handover.to_reviewer_id) ?? "—",
                })}
              </p>

              <blockquote className="border-border border-l-2 pl-3 text-sm text-muted">
                {handover.reason}
              </blockquote>

              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/submissions/$submissionId"
                  params={{ submissionId: handover.submission_id }}
                  className="text-sm text-primary hover:underline"
                >
                  {t("handovers.viewSubmission")}
                </Link>
                {mayDecide && (
                  <span className="ml-auto flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => decide.mutate({ id: handover.id, approve: true })}
                      disabled={decide.isPending}
                      tooltip={t("tooltips.approveHandover")}
                    >
                      {t("handovers.approve")}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => decide.mutate({ id: handover.id, approve: false })}
                      disabled={decide.isPending}
                      tooltip={t("tooltips.rejectHandover")}
                    >
                      {t("handovers.reject")}
                    </Button>
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {query.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void query.fetchNextPage()}
            tooltip={t("tooltips.loadMore")}
          >
            {t("table.loadMore")}
          </Button>
        </div>
      )}
    </>
  );
}
